import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import AmbientField from "@/components/AmbientField";
import telegramLogo from "@/assets/telegram-logo.png";

const SUPPORTED_LANGS = [
  { code: "ru", label: "RU" },
  { code: "en", label: "EN" },
];

function TelegramGlyph() {
  return (
    <img
      src={telegramLogo}
      alt=""
      aria-hidden="true"
      className="w-6 h-6 rounded-full object-contain"
    />
  );
}

const parseWaitSeconds = (detail) => {
  const text = String(detail || "");
  const m = text.match(/(\d+)s/);
  if (!m) return 0;
  return Math.max(0, Number(m[1] || 0));
};

const maskEmail = (email) => {
  const raw = String(email || "").trim();
  if (!raw.includes("@")) return raw;
  const [name, domain] = raw.split("@", 2);
  if (name.length <= 2) return `${name[0] || ""}***@${domain}`;
  return `${name.slice(0, 2)}***${name.slice(-1)}@${domain}`;
};

export default function WebAuthScreen({ api, onAuthSuccess }) {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [authEmail, setAuthEmail] = useState("");

  const [code, setCode] = useState("");
  const [codeError, setCodeError] = useState("");
  const [codeRequestLoading, setCodeRequestLoading] = useState(false);
  const [codeVerifyLoading, setCodeVerifyLoading] = useState(false);
  const [resendLeft, setResendLeft] = useState(0);

  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const prevHtmlOverflow = html.style.overflow;
    const prevBodyOverflow = body.style.overflow;
    const prevBodyTouchAction = body.style.touchAction;
    const prevBodyOverscroll = body.style.overscrollBehavior;
    const prevBodyHeight = body.style.height;
    const prevHtmlHeight = html.style.height;

    html.style.overflow = "hidden";
    body.style.overflow = "hidden";
    body.style.touchAction = "none";
    body.style.overscrollBehavior = "none";
    body.style.height = "100%";
    html.style.height = "100%";

    return () => {
      html.style.overflow = prevHtmlOverflow;
      body.style.overflow = prevBodyOverflow;
      body.style.touchAction = prevBodyTouchAction;
      body.style.overscrollBehavior = prevBodyOverscroll;
      body.style.height = prevBodyHeight;
      html.style.height = prevHtmlHeight;
    };
  }, []);

  useEffect(() => {
    const persisted = localStorage.getItem("i18nextLng");
    if (!persisted) {
      i18n.changeLanguage("ru");
    }
  }, [i18n]);

  const currentLang = useMemo(() => (i18n.language || "ru").startsWith("en") ? "en" : "ru", [i18n.language]);
  const query = useMemo(() => new URLSearchParams(location.search || ""), [location.search]);
  const isCodePage = location.pathname.startsWith("/auth/code");
  const codeEmail = String(query.get("email") || "").trim();
  const codePurpose = String(query.get("purpose") || "auth").trim();
  const codeLang = String(query.get("lang") || "").trim().toLowerCase();

  useEffect(() => {
    if (!isCodePage) return;
    if (codeLang.startsWith("ru")) i18n.changeLanguage("ru");
    else if (codeLang.startsWith("en")) i18n.changeLanguage("en");
  }, [isCodePage, codeLang, i18n]);

  const text = {
    en: {
      title: "Open LightPay",
      subtitle: "Sign in to continue",
      email: "Email",
      telegram: "Continue with Telegram",
      or: "or",
      requestCode: "Get code",
      verifyCode: "Verify",
      codeTitle: "Enter\nconfirmation code",
      codeSentTo: "Sent to",
      resendIn: "Send again in",
      resendNow: "Send again",
      seconds: "seconds",
      codePlaceholder: "6-digit code",
      codeInvalid: "Invalid code",
    },
    ru: {
      title: "Вход в LightPay",
      subtitle: "Авторизуйтесь для продолжения",
      email: "Почта",
      telegram: "Продолжить через Telegram",
      or: "или",
      requestCode: "Получить код",
      verifyCode: "Подтвердить",
      codeTitle: "Введите\nкод подтверждения",
      codeSentTo: "Отправлен на",
      resendIn: "Отправить повторно через",
      resendNow: "Отправить повторно",
      seconds: "секунд",
      codePlaceholder: "Код из 6 цифр",
      codeInvalid: "Неверный код",
    },
  }[currentLang];

  useEffect(() => {
    if (!isCodePage) return;
    setCode("");
    setCodeError("");
    setError("");
    setResendLeft(60);
  }, [isCodePage, codeEmail, codePurpose]);

  useEffect(() => {
    if (!isCodePage) return;
    if (resendLeft <= 0) return;
    const id = window.setInterval(() => {
      setResendLeft((prev) => Math.max(0, prev - 1));
    }, 1000);
    return () => window.clearInterval(id);
  }, [isCodePage, resendLeft]);

  const applyAuth = (payload) => {
    const token = String(payload?.token || "").trim();
    const user = payload?.user || null;
    if (!token || !user) throw new Error("Invalid auth response");
    onAuthSuccess(token, user);
  };

  const goToCodePage = (email, purpose) => {
    const params = new URLSearchParams();
    params.set("email", email);
    params.set("purpose", purpose);
    params.set("lang", currentLang);
    navigate(`/auth/code?${params.toString()}`, { replace: false });
  };

  const requestCode = async (purpose, email, { openCodePage = true } = {}) => {
    const normalized = String(email || "").trim();
    if (!normalized) return;
    setCodeRequestLoading(true);
    setCodeError("");
    setError("");
    try {
      await api.post("/auth/email/code/request", {
        email: normalized,
        purpose,
        language: currentLang,
      });
      if (openCodePage) {
        goToCodePage(normalized, purpose);
      }
      setResendLeft(60);
    } catch (err) {
      const detail = String(err?.response?.data?.detail || "Failed to send code");
      const waitSeconds = parseWaitSeconds(detail);
      if (waitSeconds > 0) {
        setResendLeft(waitSeconds);
      }
      if (openCodePage) {
        setError(detail);
      } else {
        setCodeError(detail);
      }
    } finally {
      setCodeRequestLoading(false);
    }
  };

  const submitAuth = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await requestCode("auth", authEmail);
    } finally {
      setLoading(false);
    }
  };

  const verifyCode = async () => {
    if (code.length !== 6) {
      setCodeError(text.codeInvalid);
      return;
    }
    setCodeVerifyLoading(true);
    setCodeError("");
    try {
      const res = await api.post("/auth/email/code/verify", {
        email: codeEmail,
        code,
        purpose: codePurpose,
      });
      applyAuth(res?.data || {});
    } catch (err) {
      setCodeError(String(err?.response?.data?.detail || text.codeInvalid));
    } finally {
      setCodeVerifyLoading(false);
    }
  };

  useEffect(() => {
    if (!isCodePage) return;
    const onKeyDown = (event) => {
      if (event.key !== "Enter") return;
      if (codeVerifyLoading) return;
      if (code.length !== 6) return;
      event.preventDefault();
      verifyCode();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isCodePage, code, codeVerifyLoading]);

  const handleTelegramAuth = async () => {
    setLoading(true);
    setError("");
    try {
      const nextPath = "/";
      const res = await api.get("/auth/telegram-login/start", {
        params: {
          mode: "login",
          next: nextPath,
          return_url: 1,
        },
      });
      const redirectUrl = String(res?.data?.url || "").trim();
      if (!redirectUrl) throw new Error("Telegram OAuth URL is empty");
      window.location.assign(redirectUrl);
    } catch (err) {
      setError(String(err?.response?.data?.detail || "Telegram login failed to start"));
      setLoading(false);
    }
  };

  const handleCodeInput = (value) => {
    const next = String(value || "").replace(/\D+/g, "").slice(0, 6);
    setCode(next);
    if (codeError) setCodeError("");
  };

  const closeCodePage = () => {
    navigate("/auth", { replace: true });
  };

  if (isCodePage) {
    return (
      <div className="app-container h-[100dvh] overflow-hidden bg-background text-foreground flex items-center justify-center p-4">
        <AmbientField />
        <div className="lp-veil lp-veil--edged lp-layer w-full max-w-md max-h-[calc(100dvh-2rem)] overflow-hidden rounded-[22px] px-4 py-4 md:px-5 md:py-5">
          <div className="flex items-start justify-between gap-3">
            <h1 className="text-[26px] md:text-[30px] font-semibold leading-[1.1] whitespace-pre-line tracking-tight">
              {text.codeTitle}
            </h1>
            <button
              type="button"
              aria-label="Close"
              className="lp-press lp-tile w-9 h-9 shrink-0 !rounded-full text-muted-foreground flex items-center justify-center text-2xl leading-none"
              onClick={closeCodePage}
            >
              ×
            </button>
          </div>

          <p className="mt-3 text-[16px] md:text-[18px] text-muted-foreground leading-tight">
            {text.codeSentTo} <span className="text-foreground font-medium break-all">{maskEmail(codeEmail)}</span>
          </p>

          <div className="mt-6">
            <input
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              value={code}
              onChange={(e) => handleCodeInput(e.target.value)}
              className="sr-only"
              maxLength={6}
              aria-label={text.codePlaceholder}
            />

            <button
              type="button"
              className="w-full"
              onClick={() => {
                const el = document.querySelector('input[aria-label="' + text.codePlaceholder + '"]');
                if (el) el.focus();
              }}
            >
              <div className="grid grid-cols-6 gap-2.5">
                {Array.from({ length: 6 }).map((_, idx) => {
                  const ch = code[idx] || "";
                  const active = idx === code.length;
                  return (
                    <div
                      key={idx}
                      className={`h-12 md:h-14 rounded-xl border flex items-center justify-center text-lg md:text-xl font-semibold ${
                        active
                          ? "border-white/65 bg-white/10"
                          : "border-transparent bg-white/8"
                      }`}
                    >
                      <span className="text-white/90">{ch || "·"}</span>
                    </div>
                  );
                })}
              </div>
            </button>
          </div>

          <div className="mt-5 text-center text-white/65 text-[15px] md:text-[16px]">
            {resendLeft > 0 ? (
              <span>
                {text.resendIn} <span className="text-white font-semibold">{resendLeft} {text.seconds}</span>
              </span>
            ) : (
              <button
                type="button"
                className="text-white font-semibold"
                disabled={codeRequestLoading}
                onClick={() => requestCode(codePurpose || "auth", codeEmail, { openCodePage: false })}
              >
                {text.resendNow}
              </button>
            )}
          </div>

          {codeError ? (
            <p className="mt-2 text-center text-destructive-fg text-sm">{codeError}</p>
          ) : null}

          <Button
            type="button"
            className="mt-5 w-full h-12 rounded-[16px] text-lg md:text-xl disabled:opacity-70"
            disabled={codeVerifyLoading || code.length !== 6}
            onClick={verifyCode}
          >
              {text.verifyCode}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="app-container flex items-center justify-center bg-background p-4">
      <AmbientField />
      <div className="lp-veil lp-veil--edged lp-layer w-full max-w-md rounded-[22px] p-5 space-y-4">
        <div className="flex items-center justify-end gap-2">
          {SUPPORTED_LANGS.map((lang) => (
            <button
              key={lang.code}
              type="button"
              onClick={() => i18n.changeLanguage(lang.code)}
              className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                currentLang === lang.code
                  ? "bg-primary text-primary-foreground border-primary"
                  : "border-[rgb(var(--lp-edge)/var(--lp-edge-a))] text-muted-foreground"
              }`}
            >
              {lang.label}
            </button>
          ))}
        </div>

        <div className="-mt-1">
          <h1 className="text-4xl font-bold tracking-[-0.032em] text-foreground">{text.title}</h1>
          <p className="text-sm text-muted-foreground mt-1">{text.subtitle}</p>
        </div>

        <form className="space-y-3" onSubmit={submitAuth}>
          <Input
            type="email"
            placeholder={text.email}
            value={authEmail}
            onChange={(e) => setAuthEmail(e.target.value)}
            required
            className="h-12"
          />
          <Button type="submit" className="w-full h-12 rounded-[16px] text-base" disabled={loading || codeRequestLoading}>
            {text.requestCode}
          </Button>
        </form>

        <div className="flex items-center gap-3 py-1">
          <span className="lp-hair h-px flex-1" />
          <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground">{text.or}</span>
          <span className="lp-hair h-px flex-1" />
        </div>

        <Button
          type="button"
          className="lp-tile w-full h-12 !rounded-[16px] text-foreground text-base font-semibold"
          disabled={loading}
          onClick={handleTelegramAuth}
        >
          <span className="inline-flex items-center gap-2 -translate-x-0.5">
            <TelegramGlyph />
            <span>{text.telegram}</span>
          </span>
        </Button>

        {error ? <p className="text-sm text-destructive-fg">{error}</p> : null}
      </div>
    </div>
  );
}
